import type { StatusTone } from "@/components/ui";
import type { SystemToast } from "@/stores/notification-store";
import type { WorkToastItem } from "@/stores/work-toast-store";
import {
  compareWorkToasts,
  isAttentionState,
  systemToastDot,
  workToastDot,
} from "./toast-status";

export const MAX_TOAST_ENTRIES = 3;

export type ToastStackEntry =
  | { kind: "work"; key: string; item: WorkToastItem }
  | { kind: "system"; key: string; toast: SystemToast }
  | { kind: "collapse"; key: "collapse" }
  | { kind: "need-you"; key: "need-you"; count: number; oldestKey: string; dots: StatusTone[] }
  | {
      kind: "more";
      key: "more";
      count: number;
      dots: StatusTone[];
      /** Hidden work item to open when revealAll is false. An expanded
       *  card's count row always closes that card. */
      expandKey: string | null;
      /** Reveal the scrollable list when hidden system notices need access. */
      revealAll?: boolean;
    };

export function systemToastKey(id: string): string {
  return `system:${id}`;
}

function dotsOf(entries: ToastStackEntry[]): StatusTone[] {
  return entries
    .slice(0, 3)
    .map((entry) =>
      entry.kind === "work"
        ? workToastDot(entry.item.state)
        : entry.kind === "system"
          ? systemToastDot(entry.toast.level)
          : null,
    )
    .filter((dot): dot is StatusTone => dot !== null);
}

/**
 * Spec §4.5: attention first, then system toasts, then the rest, three slots
 * total. System toasts carry no `stateChangedAt`, so — unlike work items,
 * which interleave by recency within their group — they always sort as a
 * block between the attention group and the plain work-item group rather
 * than being interleaved with either by time.
 *
 * Past three total entries (and with nothing expanded), slot 3 becomes a
 * count row for *everything* hidden, so a system toast is folded into a
 * count instead of silently disappearing:
 *  - more than three attention entries: "+N need you" (opens the oldest
 *    attention entry).
 *  - otherwise: "+N more" (opens the newest hidden work item if there is
 *    one; hidden system notices reveal the full scrollable list).
 * While a card is open, the rest collapses into its own "+N more" whose
 * click always just closes the card back to the pill row.
 */
export function buildToastStackEntries({
  items,
  systemToasts,
  expandedKey,
  showAll = false,
}: {
  items: WorkToastItem[];
  systemToasts: SystemToast[];
  expandedKey: string | null;
  showAll?: boolean;
}): ToastStackEntry[] {
  const attention = items.filter((item) => isAttentionState(item.state)).sort(compareWorkToasts);
  const rest = items.filter((item) => !isAttentionState(item.state)).sort(compareWorkToasts);
  const ordered: ToastStackEntry[] = [
    ...attention.map((item) => ({ kind: "work" as const, key: item.key, item })),
    ...[...systemToasts].sort((a, b) => Number(Boolean(b.persistent)) - Number(Boolean(a.persistent)))
      .map((toast) => ({ kind: "system" as const, key: systemToastKey(toast.id), toast })),
    ...rest.map((item) => ({ kind: "work" as const, key: item.key, item })),
  ];

  if (expandedKey) {
    const expanded = ordered.find((entry) => entry.key === expandedKey);
    if (expanded) {
      const others = ordered.filter((entry) => entry.key !== expandedKey);
      if (others.length === 0) return [expanded];
      return [
        expanded,
        { kind: "more", key: "more", count: others.length, dots: dotsOf(others), expandKey: null },
      ];
    }
  }

  if (showAll && ordered.length > MAX_TOAST_ENTRIES) return [...ordered, { kind: "collapse", key: "collapse" }];

  if (ordered.length > MAX_TOAST_ENTRIES) {
    const visible = ordered.slice(0, MAX_TOAST_ENTRIES - 1);
    const hidden = ordered.slice(MAX_TOAST_ENTRIES - 1);
    if (hidden.some((entry) => entry.kind === "system")) {
      return [...visible, { kind: "more", key: "more", count: hidden.length, dots: dotsOf(hidden), expandKey: null, revealAll: true }];
    }

    if (attention.length > MAX_TOAST_ENTRIES) {
      const oldestAttention = attention[attention.length - 1]!;
      return [
        ...visible,
        {
          kind: "need-you",
          key: "need-you",
          count: hidden.length,
          oldestKey: oldestAttention.key,
          dots: dotsOf(hidden),
        },
      ];
    }

    const newestHidden = hidden[0]!;
    return [
      ...visible,
      {
        kind: "more",
        key: "more",
        count: hidden.length,
        dots: dotsOf(hidden),
        expandKey: newestHidden.kind === "work" ? newestHidden.key : null,
      },
    ];
  }

  return ordered.slice(0, MAX_TOAST_ENTRIES);
}
